/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useRef, useState } from 'react'

import { makeStyles } from '@mui/styles'

import { useSelector } from 'react-redux'
import IconCreateFolder from '@assets/images/icons/add_folder.svg'
import lang from '@/lang'
import PropTypes from 'prop-types'
import PerfectScrollbar from 'react-perfect-scrollbar'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { FILE_LIST_FILTER, FORBIDDEN_FILENAME_CHARACTERS } from '@/modules/base/util/constants'
import theme from '@/theme'
import FolderNavTree from '@/modules/file/components/folder-nav/folder-nav-tree'
import ThemeInterface from '@/theme-interface'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import { setFolder } from '@/redux/ducks/document/list'
import clsx from 'clsx'
import { createFolder } from '@/redux/ducks/lists/requests/documentList'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'
import IconInfo from '@assets/images/icons/info.svg'
import CreateFolderModal from '@/modules/document/components/folder/create-folder-modal'

const {
  documents: {
    folder: {
      nameTooShort,
      create,
      createTitle,
      createSubTitle,
      createName,
      createNewFolder,
    },
  },
} = lang

const useStyles = makeStyles((theme: ThemeInterface) => ({
  folderNav: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 360,
    maxWidth: 800,
    borderRight: theme.border,
    zIndex: 2,
  },
  overviewItem: {
    padding: CssHelper.px(20, 30),
    borderBottom: theme.border,
    fontWeight: theme.fontWeights.medium,
    display: 'flex',
    flexDirection: 'column',
    '&.active': {
      background: theme.backgroundColor,
      color: theme.primary,
    },
  },
  subHeader: {
    cursor: 'pointer',
    fontSize: theme.fontSizes.small,
  },
  content: {
    flexGrow: 1,
    maxHeight: `calc(100vh - 300px - ${theme.menuHeight}px)`,
    '& .ps__rail-y': {
      zIndex: 1,
    },
  },
  compactContent: {
    maxHeight: 'none',
  },
  folders: {
    display: 'flex',
    flexDirection: 'column',
    background: theme.white,
    flexWrap: 'wrap',
    position: 'relative',
    overflow: 'hidden',
  },
  footer: {
    borderTop: theme.border,
    minHeight: 80,
    color: theme.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      fontWeight: theme.fontWeights.medium,
    },
  },
  compactFooter: {
    minHeight: 'unset',
    padding: '24px 0',
  },
  emptyFolders: {
    padding: CssHelper.px(20, 30),
    color: theme.textLight,
    fontSize: theme.fontSizes.small,
  },
  createIcon: {
    marginRight: 10,
  },
  readOnlyTooltipText: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: theme.fontSizes.smaller,
    margin: 0,
    marginTop: 5,
    color: theme.lighterBlue,
    fontStyle: 'italic',
    cursor: 'pointer',
  },
}))

type Props = {
  filter: keyof typeof FILE_LIST_FILTER,
  accessDenied?: boolean,
  compactSpacing?: boolean,
}

const SCROLL_SPEED = 8
const SCROLL_INTERVAL = 30
const EDGE_THRESHOLD = 30

const FolderNav = (props: Props) => {
  const { filter, accessDenied, compactSpacing = false } = props

  const classes = useStyles(props)

  const [nameModalOpen, setNameModalOpen] = useState(false)

  const globalTotalCount = useSelector(state => state.lists.documents.meta.globalTotalCount || state.lists.documents.meta.totalCount)
  const isReadOnly = useSelector((state: any) => state.document.list.isReadOnly)

  const containerRef = useRef(null)
  const scrollInterval = useRef(null)
  const [isScrolling, setIsScrolling] = useState(false)

  const onCreate = (name, accessList) => {
    setNameModalOpen(false)
    createFolder(name, folders, filter, folders, null, accessList)
  }

  const {
    currentFolder,
    folders,
    foldersLoaded,
  } = useSelector((state: any) => state.document.list)

  const showAllDocuments = () => {
    setFolder(folders)
  }

  const shownFolders = useMemo(() => folders.children, [filter, folders])
  const showEmptyFolders = !shownFolders.length && foldersLoaded && !accessDenied

  if (!currentFolder?.children?.length && currentFolder?.isSearch) {
    return null
  }

  const onItemHover = (folderRef) => {
    if (!containerRef.current || !folderRef) {
      stopScrolling()
      return
    }

    const rect = folderRef.getBoundingClientRect()
    const { top, bottom } = rect
    const containerRect = containerRef.current._container.getBoundingClientRect()
    const { top: containerTop, bottom: containerBottom } = containerRect

    if (top - containerTop < EDGE_THRESHOLD) {
      startScrolling(-SCROLL_SPEED)
    } else if (containerBottom - bottom < EDGE_THRESHOLD) {
      startScrolling(SCROLL_SPEED)
    } else {
      stopScrolling()
    }
  }

  const startScrolling = (speed) => {
    if (isScrolling && scrollInterval.current) return

    setIsScrolling(true)
    scrollInterval.current = setInterval(() => {
      if (containerRef.current) {
        const scrollElement = containerRef.current._container // PerfectScrollbar internal container
        scrollElement.scrollBy(0, speed)
      }
    }, SCROLL_INTERVAL)
  }

  const stopScrolling = () => {
    setIsScrolling(false)
    if (scrollInterval.current) {
      clearInterval(scrollInterval.current)
      scrollInterval.current = null
    }
  }

  return (
    <div className={classes.folderNav}>
      <div className={clsx(classes.overviewItem, { active: !currentFolder.name })}>
        <span className={classes.subHeader} data-qs-id='folder-nav-show-all-documents' onClick={showAllDocuments}>{lang.documents.folder.showAllDocuments} ({globalTotalCount})</span>
        {isReadOnly && (
          <RichTooltip body={lang.documents.readOnlyModeTooltipBody} width='500px' placement={'top'}>
            <p className={classes.readOnlyTooltipText}><SVGIcon width={12} height={12} svg={IconInfo} />{lang.documents.readOnlyModeTooltipText}</p>
          </RichTooltip>
        )}
      </div>
      <PerfectScrollbar
        ref={containerRef}
        className={clsx(classes.content, compactSpacing && classes.compactContent)}
        options={{ suppressScrollX: true }}
        onMouseLeave={stopScrolling}
      >
        {accessDenied && (
          <div className={classes.emptyFolders}>
            {lang.documents.folder.accessDenied}
          </div>
        )}
        {shownFolders.length
          ? <div className={classes.folders}>
            {shownFolders.map((folder, i) => (
              <FolderNavTree onHover={onItemHover} key={`folderNavTree#${i}`}
                rootLevel={i + 1 < shownFolders.length}
                folder={folder}
                activeFilter={filter}
              />),
            )}
          </div>
          : null}
        {showEmptyFolders && (
          <div className={classes.emptyFolders}>
            {lang.documents.folder.emptyStateText}
          </div>
        )}
      </PerfectScrollbar>

      {!isReadOnly && <div className={clsx(classes.footer, compactSpacing && classes.compactFooter)} onClick={() => setNameModalOpen(true)}>
        <SVGIcon id='createFolderFooterIcon' className={classes.createIcon} svg={IconCreateFolder} fill={theme.primary} width='16px' height='16px' /><span>{createNewFolder}</span>
        {nameModalOpen && <CreateFolderModal value={createName}
          invalidCharacters={FORBIDDEN_FILENAME_CHARACTERS}
          open
          onSubmit={onCreate}
          onClose={() => setNameModalOpen(false)}
          title={createTitle}
          subTitle={createSubTitle}
          emptyErrorMessage={nameTooShort}
          confirmButtonLabel={create} />}
      </div>}
    </div>
  )
}

FolderNav.propTypes = {
  filter: PropTypes.string,
  isLoading: PropTypes.bool,
  accessDenied: PropTypes.bool,
  compactSpacing: PropTypes.bool,
}

export default FolderNav
